import { useEffect } from 'react';
import { Box, CircularProgress, ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { queryClient } from './api/queryClient';
import { aquaRefineTheme } from './theme/theme';
import { router as browserRouter } from './router/router';
import { useAuthStore } from './store/auth.store';

export interface AppProps {
  /** Defaults to the live app's browser router; overridable (e.g. a memory router) for tests. */
  router?: ReturnType<typeof createBrowserRouter>;
}

function SessionLoadingScreen() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress size={28} />
    </Box>
  );
}

export function App({ router = browserRouter }: AppProps) {
  const status = useAuthStore((s) => s.status);

  // Silently restore a session from the httpOnly refresh cookie before
  // rendering the authenticated shell — see RequireAuth for the route guard
  // that reads the resulting status. The api client has its own timeout
  // (see api/client.ts) so this should always settle well within a few
  // seconds either way; this hard cap is just a safety net so a stuck
  // backend can never leave the whole app parked on the boot spinner
  // instead of falling through to the login page.
  useEffect(() => {
    void useAuthStore.getState().initSession();
    const forceUnauthenticated = setTimeout(() => {
      if (useAuthStore.getState().status === 'loading') {
        useAuthStore.getState().clearSession();
      }
    }, 12_000);
    return () => clearTimeout(forceUnauthenticated);
  }, []);

  const booting = status === 'idle' || status === 'loading';

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={aquaRefineTheme}>
        <CssBaseline />
        {booting ? <SessionLoadingScreen /> : <RouterProvider router={router} />}
      </ThemeProvider>
    </QueryClientProvider>
  );
}
